import { useCallback, useMemo, useRef, useState } from 'react';

import { AimOutlined, EnvironmentOutlined } from '@ant-design/icons';
import {
  Circle,
  GoogleMap,
  InfoWindow,
  Marker,
  useLoadScript,
} from '@react-google-maps/api';
import { Skeleton, Tag, Typography } from 'antd';
import { useTranslation } from 'react-i18next';

import { GOOGLE_MAP_API_KEY } from 'config/Config';

import { AttendanceUserTurnDetail } from '../types';

const { Text } = Typography;

const BASE = 'scenes.kit.dashboard_alternative_mode.components.evidence_modal';

const DISTANCE_THRESHOLD_METERS = 100;
const MAP_ZOOM = 17;
const MAP_HEIGHT = 260;

const MARKER_ICONS = {
  store: 'http://maps.google.com/mapfiles/ms/icons/purple-dot.png',
  checkin: 'http://maps.google.com/mapfiles/ms/icons/green-dot.png',
  checkout: 'http://maps.google.com/mapfiles/ms/icons/red-dot.png',
} as const;

const INFO_COLORS = {
  store: '#8E2DD2',
  checkin: '#2ECC71',
  checkout: '#FF4A4A',
} as const;

type MarkerKey = 'store' | 'checkin' | 'checkout';

interface LatLng {
  lat: number;
  lng: number;
}

function haversineMeters(a: LatLng, b: LatLng): number {
  const R = 6371000;
  const toRad = (deg: number) => (deg * Math.PI) / 180;
  const dLat = toRad(b.lat - a.lat);
  const dLng = toRad(b.lng - a.lng);
  const sinDLat = Math.sin(dLat / 2);
  const sinDLng = Math.sin(dLng / 2);
  const aVal =
    sinDLat * sinDLat +
    Math.cos(toRad(a.lat)) * Math.cos(toRad(b.lat)) * sinDLng * sinDLng;
  return R * 2 * Math.atan2(Math.sqrt(aVal), Math.sqrt(1 - aVal));
}

interface EvidenceMapProps {
  detail: AttendanceUserTurnDetail;
}

function EvidenceMap({ detail }: EvidenceMapProps) {
  const { t } = useTranslation();
  const { isLoaded } = useLoadScript({ googleMapsApiKey: GOOGLE_MAP_API_KEY });
  const [selected, setSelected] = useState<MarkerKey | null>(null);
  const mapRef = useRef<google.maps.Map | null>(null);

  const initialCenter = useRef<LatLng | null>(null);

  const onMapLoad = useCallback((map: google.maps.Map) => {
    mapRef.current = map;
    // Fija el centro inicial una sola vez de forma imperativa,
    // así el prop `center` de GoogleMap no interfiere en re-renders.
    if (initialCenter.current) {
      map.setCenter(initialCenter.current);
      map.setZoom(MAP_ZOOM);
    }
  }, []);

  const panTo = useCallback((pos: LatLng) => {
    mapRef.current?.panTo(pos);
    mapRef.current?.setZoom(MAP_ZOOM);
  }, []);

  const storePos: LatLng | null = detail.store.location
    ? {
        lat: detail.store.location.latitude,
        lng: detail.store.location.longitude,
      }
    : null;

  const checkinPos: LatLng | null = detail.checkIn.location
    ? {
        lat: detail.checkIn.location.latitude,
        lng: detail.checkIn.location.longitude,
      }
    : null;

  const checkoutPos: LatLng | null = detail.checkOut.location
    ? {
        lat: detail.checkOut.location.latitude,
        lng: detail.checkOut.location.longitude,
      }
    : null;

  const hasAnyLocation = storePos || checkinPos || checkoutPos;

  const center = useMemo<LatLng>(() => {
    if (checkinPos) return checkinPos;
    if (checkoutPos) return checkoutPos;
    if (storePos) return storePos;
    return { lat: 0, lng: 0 };
  }, [checkinPos, checkoutPos, storePos]);

  // Guarda el centro inicial solo la primera vez (cuando aún es null).
  if (initialCenter.current === null) {
    initialCenter.current = center;
  }

  if (!hasAnyLocation) {
    return (
      <div className="kit-evidence-modal__map-placeholder kit-evidence-modal__map-placeholder--no-location">
        <EnvironmentOutlined className="kit-evidence-modal__map-placeholder-icon" />
        <Text className="kit-evidence-modal__map-placeholder-label">
          {t(`${BASE}.geolocation.no_location`)}
        </Text>
      </div>
    );
  }

  if (!isLoaded) {
    return (
      <Skeleton.Image
        active
        style={{ width: '100%', height: MAP_HEIGHT, borderRadius: 10 }}
      />
    );
  }

  const distanceLabel = (pos: LatLng): string | null => {
    if (!storePos) return null;
    const meters = Math.round(haversineMeters(storePos, pos));
    const key =
      meters > DISTANCE_THRESHOLD_METERS
        ? `${BASE}.geolocation.distance_exceeded`
        : `${BASE}.geolocation.distance_label`;
    return t(key, { distance: meters });
  };

  const infoWindowOffset = 0.0003;

  return (
    <div>
      <GoogleMap
        zoom={MAP_ZOOM}
        mapContainerClassName="kit-evidence-modal__map"
        onClick={() => setSelected(null)}
        onLoad={onMapLoad}
        options={{
          fullscreenControl: true,
          fullscreenControlOptions: {
            position: google.maps.ControlPosition.BOTTOM_RIGHT,
          },
          streetViewControl: false,
          mapTypeControl: false,
        }}
      >
        {/* Tienda */}
        {storePos && (
          <>
            <Marker
              position={storePos}
              icon={{ url: MARKER_ICONS.store }}
              onClick={() =>
                setSelected((p) => (p === 'store' ? null : 'store'))
              }
            />
            <Circle
              center={storePos}
              radius={DISTANCE_THRESHOLD_METERS}
              options={{
                strokeColor: '#0000FF',
                strokeOpacity: 0.5,
                strokeWeight: 2,
                fillColor: '#0000FF',
                fillOpacity: 0.15,
              }}
            />
            {selected === 'store' && (
              <InfoWindow
                position={{
                  lat: storePos.lat + infoWindowOffset,
                  lng: storePos.lng,
                }}
                onCloseClick={() => setSelected(null)}
              >
                <div className="kit-evidence-modal__info-window">
                  <span
                    className="kit-evidence-modal__info-window-title"
                    style={{ color: INFO_COLORS.store }}
                  >
                    {t(`${BASE}.geolocation.store`)}
                  </span>
                  <span>{detail.store.name}</span>
                </div>
              </InfoWindow>
            )}
          </>
        )}

        {/* Check-in */}
        {checkinPos && (
          <>
            <Marker
              position={checkinPos}
              icon={{ url: MARKER_ICONS.checkin }}
              onClick={() =>
                setSelected((p) => (p === 'checkin' ? null : 'checkin'))
              }
            />
            {selected === 'checkin' && (
              <InfoWindow
                position={{
                  lat: checkinPos.lat + infoWindowOffset,
                  lng: checkinPos.lng,
                }}
                onCloseClick={() => setSelected(null)}
              >
                <div className="kit-evidence-modal__info-window">
                  <span
                    className="kit-evidence-modal__info-window-title"
                    style={{ color: INFO_COLORS.checkin }}
                  >
                    {t(`${BASE}.geolocation.checkin`)}
                  </span>
                  {distanceLabel(checkinPos) && (
                    <span
                      className="kit-evidence-modal__info-window-distance"
                      style={{
                        color:
                          storePos &&
                          haversineMeters(storePos, checkinPos) >
                            DISTANCE_THRESHOLD_METERS
                            ? '#ff4d4f'
                            : undefined,
                      }}
                    >
                      {distanceLabel(checkinPos)}
                    </span>
                  )}
                </div>
              </InfoWindow>
            )}
          </>
        )}

        {/* Check-out */}
        {checkoutPos && (
          <>
            <Marker
              position={checkoutPos}
              icon={{ url: MARKER_ICONS.checkout }}
              onClick={() =>
                setSelected((p) => (p === 'checkout' ? null : 'checkout'))
              }
            />
            {selected === 'checkout' && (
              <InfoWindow
                position={{
                  lat: checkoutPos.lat + infoWindowOffset,
                  lng: checkoutPos.lng,
                }}
                onCloseClick={() => setSelected(null)}
              >
                <div className="kit-evidence-modal__info-window">
                  <span
                    className="kit-evidence-modal__info-window-title"
                    style={{ color: INFO_COLORS.checkout }}
                  >
                    {t(`${BASE}.geolocation.checkout`)}
                  </span>
                  {distanceLabel(checkoutPos) && (
                    <span
                      className="kit-evidence-modal__info-window-distance"
                      style={{
                        color:
                          storePos &&
                          haversineMeters(storePos, checkoutPos) >
                            DISTANCE_THRESHOLD_METERS
                            ? '#ff4d4f'
                            : undefined,
                      }}
                    >
                      {distanceLabel(checkoutPos)}
                    </span>
                  )}
                </div>
              </InfoWindow>
            )}
          </>
        )}
      </GoogleMap>

      {/* ── Pills localizadoras ── */}
      <div className="kit-evidence-modal__map-locators">
        {(
          [
            {
              key: 'store' as MarkerKey,
              pos: storePos,
              label: t(`${BASE}.geolocation.locate_store`),
              color: INFO_COLORS.store,
            },
            {
              key: 'checkin' as MarkerKey,
              pos: checkinPos,
              label: t(`${BASE}.geolocation.locate_checkin`),
              color: INFO_COLORS.checkin,
            },
            {
              key: 'checkout' as MarkerKey,
              pos: checkoutPos,
              label: t(`${BASE}.geolocation.locate_checkout`),
              color: INFO_COLORS.checkout,
            },
          ] as const
        ).map(({ key, pos, label, color }) => (
          <Tag
            key={key}
            className={[
              'kit-evidence-modal__map-locator-tag',
              !pos ? 'kit-evidence-modal__map-locator-tag--disabled' : '',
            ]
              .filter(Boolean)
              .join(' ')}
            onClick={() => {
              if (!pos) return;
              panTo(pos);
              setSelected(key);
            }}
          >
            <AimOutlined
              style={{ color: pos ? color : undefined }}
              aria-hidden="true"
            />
            {label}
          </Tag>
        ))}
      </div>
    </div>
  );
}

export default EvidenceMap;
